import { useEffect, useRef, useState } from "react";
import type { ConversationMessage, ConversationThreadView } from "../../../../store/types";

const QUICK_TEMPLATES = [
  "感謝您的聯繫！我們的運營團隊正在處理您的請求，請稍候。",
  "攝影師已確認您的預訂，請在約定時間前往指定集合地點。",
  "提醒：平台所有費用請透過官方渠道付款，請勿直接給付現金。",
  "如需更改拍攝日期，請至平台發出改期申請，我們將盡快安排。",
  "您的攝影服務行程已更新，請查看最新集合地點與時間。",
];

const RISK_FLAG_OPTIONS: { level: "low" | "medium" | "high"; reason: string }[] = [
  { level: "medium", reason: "疑似要求平台外付款" },
  { level: "high", reason: "疑似分享私人聯絡方式（LINE/WhatsApp）" },
  { level: "medium", reason: "疑似要求額外服務" },
  { level: "low", reason: "疑似在平台外議價" },
];

interface Props {
  thread: ConversationThreadView | null;
  messages: ConversationMessage[];
  loadingMessages: boolean;
  onSend: (body: string, messageType?: ConversationMessage["message_type"]) => Promise<void>;
  onFlag: (threadId: string, riskLevel: string, reason: string) => Promise<void>;
  onChangeStatus: (threadId: string, status: "open" | "closed" | "flagged") => Promise<void>;
}

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString("zh-TW", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const SENDER_LABEL: Record<string, string> = { admin: "運營", photographer: "攝影師", customer: "客戶", system: "系統" };
const SENDER_COLOR: Record<string, string> = {
  admin: "bg-[#2C2C2C] text-white",
  photographer: "bg-amber-100 text-amber-800",
  customer: "bg-emerald-100 text-emerald-800",
  system: "bg-teal-100 text-teal-700",
};

/** Detect if a message was auto-generated by the dispatch/relay backend */
function isDispatchNotice(msg: ConversationMessage): boolean {
  return (
    msg.message_type === "system_notice" &&
    (msg.sender_type === "system" ||
      (msg.sender_type === "admin" && msg.sender_id === "system"))
  );
}

export default function RelayMessagePanel({ thread, messages, loadingMessages, onSend, onFlag, onChangeStatus }: Props) {
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showFlagMenu, setShowFlagMenu] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || !thread || sending) return;
    setSending(true);
    await onSend(trimmed, "text");
    setInput("");
    setSending(false);
  };

  if (!thread) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center bg-[#FAFAF8]">
        <div className="w-10 h-10 flex items-center justify-center text-[#2C2C2C]/15 mb-3"><i className="ri-chat-3-line text-4xl" /></div>
        <p className="text-sm text-[#2C2C2C]/30 font-light tracking-wide">選擇訊息串開始回覆</p>
      </div>
    );
  }

  const isRisky = thread.risk_level !== "none";
  // First dispatch notice (if any) — show as banner
  const firstDispatchNotice = messages.find(isDispatchNotice);

  return (
    <div className="flex-1 flex flex-col min-w-0 bg-[#FAFAF8] h-full">
      {/* Thread header */}
      <div className="px-5 py-3.5 border-b border-[#E8E0D5] bg-white flex items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-[#2C2C2C] truncate">{thread.subject || "（無主旨）"}</span>
            {isRisky && <span className={`px-2 py-0.5 rounded text-[10px] font-medium whitespace-nowrap shrink-0 ${thread.risk_level === "high" ? "bg-red-100 text-red-600" : thread.risk_level === "medium" ? "bg-orange-100 text-orange-600" : "bg-yellow-100 text-yellow-600"}`}>⚑ {thread.risk_level}</span>}
            <span className={`px-2 py-0.5 rounded text-[10px] font-light whitespace-nowrap shrink-0 ${thread.status === "open" ? "bg-emerald-100 text-emerald-700" : thread.status === "flagged" ? "bg-red-100 text-red-600" : "bg-[#F0EAE2] text-[#8B7355]"}`}>
              {thread.status === "open" ? "進行中" : thread.status === "closed" ? "已關閉" : "已標記"}
            </span>
          </div>
          <div className="flex items-center gap-2 mt-0.5">
            {thread.order_number && <span className="text-[10px] text-[#2C2C2C]/40">{thread.order_number}</span>}
            {thread.customer_name && <span className="text-[10px] text-[#2C2C2C]/30">{thread.customer_name}</span>}
            {thread.selected_city && <span className="text-[10px] text-[#2C2C2C]/30">{thread.selected_city}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="relative">
            <button onClick={() => setShowFlagMenu(!showFlagMenu)} title="標記風險"
              className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
              <i className="ri-flag-line text-sm" />
            </button>
            {showFlagMenu && (
              <div className="absolute right-0 top-9 w-56 bg-white border border-[#E8E0D5] rounded-lg z-20 py-1">
                {RISK_FLAG_OPTIONS.map((opt) => (
                  <button key={opt.reason} onClick={async () => { await onFlag(thread.id, opt.level, opt.reason); setShowFlagMenu(false); }}
                    className="w-full text-left px-3 py-2 text-xs text-[#2C2C2C]/70 hover:bg-[#F7F6F3] transition-colors cursor-pointer flex items-center gap-2">
                    <span className={`text-[9px] px-1 rounded ${opt.level === "high" ? "bg-red-100 text-red-600" : opt.level === "medium" ? "bg-orange-100 text-orange-600" : "bg-yellow-100 text-yellow-600"}`}>{opt.level}</span>
                    {opt.reason}
                  </button>
                ))}
              </div>
            )}
          </div>
          {thread.status === "open"
            ? <button onClick={() => onChangeStatus(thread.id, "closed")} className="px-2.5 py-1 rounded-md text-xs font-light border border-[#E8E0D5] text-[#2C2C2C]/50 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap">關閉串</button>
            : <button onClick={() => onChangeStatus(thread.id, "open")} className="px-2.5 py-1 rounded-md text-xs font-light border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-colors cursor-pointer whitespace-nowrap">重新開啟</button>
          }
        </div>
      </div>

      {/* Dispatch notice callout — shown inline above messages */}
      {firstDispatchNotice && (
        <div className="px-5 pt-3">
          <div className="flex items-start gap-2.5 px-3.5 py-2.5 bg-teal-50 border border-teal-200 rounded-lg">
            <div className="w-4 h-4 flex items-center justify-center text-teal-500 shrink-0 mt-0.5">
              <i className="ri-send-plane-2-line text-sm" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 mb-0.5">
                <span className="text-[10px] font-medium text-teal-700">排班系統通知</span>
                <span className="text-[9px] text-teal-500/70 font-light">{formatTime(firstDispatchNotice.created_at)}</span>
              </div>
              <p className="text-[11px] text-teal-700/80 font-light leading-relaxed">{firstDispatchNotice.body}</p>
            </div>
          </div>
        </div>
      )}

      {/* Messages list */}
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        {loadingMessages ? (
          <div className="flex justify-center py-12"><p className="text-xs text-[#2C2C2C]/30">載入訊息中...</p></div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-8 h-8 flex items-center justify-center text-[#2C2C2C]/15 mb-2"><i className="ri-message-line text-2xl" /></div>
            <p className="text-xs text-[#2C2C2C]/30 font-light">尚無訊息 — 傳送第一則訊息</p>
          </div>
        ) : messages.map((msg) => {
          const isAdmin = msg.sender_type === "admin" || msg.sender_type === "system";
          const hasRisk = Array.isArray(msg.risk_flags) && msg.risk_flags.length > 0;
          const isDispatch = isDispatchNotice(msg);
          return (
            <div key={msg.id} className={`flex flex-col gap-1 ${isAdmin ? "items-end" : "items-start"}`}>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded text-[9px] font-medium ${SENDER_COLOR[msg.sender_type] ?? "bg-gray-100 text-gray-600"}`}>
                  {SENDER_LABEL[msg.sender_type] ?? msg.sender_type}
                </span>
                {isDispatch && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-teal-100 text-teal-600">
                    <i className="ri-robot-line mr-0.5 text-[8px]" />排班通知
                  </span>
                )}
                <span className="text-[9px] text-[#2C2C2C]/25">{formatTime(msg.created_at)}</span>
                {msg.is_blocked && <span className="text-[9px] text-red-500 font-medium">⊘ 已封鎖</span>}
                {hasRisk && !msg.is_blocked && <span className="text-[9px] text-orange-400">⚑ {(msg.risk_flags as string[]).join(" · ")}</span>}
              </div>
              <div className={`max-w-md px-3.5 py-2.5 rounded-lg text-xs font-light leading-relaxed ${
                msg.is_blocked ? "bg-red-50 border border-red-200 text-red-400 line-through" :
                isDispatch ? "bg-teal-50 border border-teal-200 text-teal-800 italic" :
                msg.message_type === "system_notice" ? "bg-[#F0EAE2] border border-[#D9C9B4] text-[#8B7355] italic" :
                isAdmin ? "bg-[#2C2C2C] text-white" : "bg-white border border-[#E8E0D5] text-[#2C2C2C]"
              }`}>
                {msg.body}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      {/* Input area */}
      <div className="border-t border-[#E8E0D5] bg-white px-4 py-3">
        <div className="flex items-start gap-2.5">
          <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSend(); }}
            placeholder="輸入訊息... (Ctrl+Enter 傳送)" rows={2} disabled={thread.status === "closed"}
            className="flex-1 px-3 py-2 text-xs font-light border border-[#E8E0D5] rounded-lg bg-[#F7F6F3] text-[#2C2C2C] placeholder-[#2C2C2C]/30 focus:outline-none focus:border-[#C8B8A6] resize-none disabled:opacity-40 text-sm" />
          <div className="flex flex-col gap-1.5">
            <button onClick={handleSend} disabled={!input.trim() || sending || thread.status === "closed"}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#2C2C2C] text-white disabled:opacity-40 hover:bg-[#444] transition-colors cursor-pointer">
              <div className="w-4 h-4 flex items-center justify-center"><i className="ri-send-plane-fill text-sm" /></div>
            </button>
            <div className="relative">
              <button onClick={() => setShowTemplates(!showTemplates)} title="快速範本"
                className="w-8 h-8 flex items-center justify-center rounded-lg border border-[#E8E0D5] text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70 hover:border-[#C8B8A6] transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center"><i className="ri-draft-line text-sm" /></div>
              </button>
              {showTemplates && (
                <div className="absolute bottom-10 right-0 w-80 bg-white border border-[#E8E0D5] rounded-lg z-20 py-1">
                  <p className="px-3 py-1.5 text-[10px] text-[#2C2C2C]/40 font-light border-b border-[#F0EAE2]">快速範本</p>
                  {QUICK_TEMPLATES.map((tpl) => (
                    <button key={tpl} onClick={() => { setInput(tpl); setShowTemplates(false); }}
                      className="w-full text-left px-3 py-2 text-xs text-[#2C2C2C]/70 hover:bg-[#F7F6F3] transition-colors cursor-pointer leading-relaxed">{tpl}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
